'use client';

import { motion } from 'motion/react';
import {
  Mail,
  Phone,
  Share2,
  ArrowRight,
  Check,
  Zap,
  Shield,
  BarChart3,
  Sparkles,
  DollarSign,
} from 'lucide-react';
import { useAppRouter } from '~/hooks';

export default function Solutions() {
  const router = useAppRouter();

  const solutions = [
    {
      icon: Mail,
      title: 'Email Extraction',
      description:
        'Extract verified business email addresses from any URL or text with our advanced AI-powered extraction engine.',
      features: [
        'Real-time email validation',
        'Domain-based filtering',
        'Bulk processing capabilities',
        'Export in multiple formats',
      ],
      gradient: 'from-blue-500 to-cyan-500',
      page: 'solutions/email-extraction',
    },
    {
      icon: Phone,
      title: 'Phone Number Extraction',
      description:
        'Capture phone numbers with international format detection and automatic validation for marketing campaigns.',
      features: [
        'International format support',
        'Mobile vs landline detection',
        'Area code identification',
        'Duplicate removal',
      ],
      gradient: 'from-purple-500 to-pink-500',
      page: 'solutions/phone-extraction',
    },
    {
      icon: Share2,
      title: 'Social Media Extraction',
      description:
        'Discover social media profiles and handles across major platforms to enhance your multi-channel outreach.',
      features: [
        'Multi-platform support',
        'Profile verification',
        'Follower count analysis',
        'Engagement metrics',
      ],
      gradient: 'from-orange-500 to-red-500',
      page: 'solutions/social-extraction',
    },
  ];

  const benefits = [
    {
      icon: Zap,
      title: 'Lightning Fast',
      description:
        'Process thousands of leads in seconds with our optimized extraction algorithms.',
    },
    {
      icon: Shield,
      title: 'GDPR Compliant',
      description:
        'All data extraction follows strict compliance guidelines and best practices.',
    },
    {
      icon: BarChart3,
      title: 'Actionable Insights',
      description:
        'Get detailed analytics and quality scores for every extracted contact.',
    },
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="px-4 py-20 md:py-32 relative overflow-hidden bg-linear-to-br from-background-secondary via-secondary to-accent dark:from-[#1e293b] dark:via-[#312e81] dark:to-[#3730a3]">
        {/* Animated Background Elements */}
        <motion.div
          className="absolute top-20 right-10 w-72 h-72 bg-linear-to-br from-primary/40 to-[#8b5cf6]/30 rounded-full opacity-60 blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            x: [0, 30, 0],
            y: [0, -30, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          className="absolute bottom-20 left-10 w-96 h-96 bg-linear-to-tr from-[#8b5cf6]/20 to-[#ec4899]/20 rounded-full opacity-60 blur-3xl"
          animate={{
            scale: [1, 1.3, 1],
            x: [0, -40, 0],
            y: [0, 40, 0],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />

        {/* Gradient Mesh Overlay */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iY3VycmVudENvbG9yIiBzdHJva2Utb3BhY2l0eT0iMC4xIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30 text-primary dark:opacity-20"></div>

        {/* Floating Sketched Elements */}
        {/* Star sketch top-left */}
        <motion.svg
          className="absolute top-10 left-10 w-16 h-16 text-primary/40"
          animate={{
            y: [0, -20, 0],
            rotate: [0, 15, 0],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <path d="M50 10 L55 40 L85 45 L60 65 L70 95 L50 80 L30 95 L40 65 L15 45 L45 40 Z" />
          <circle cx="50" cy="50" r="3" fill="currentColor" />
        </motion.svg>

        {/* Lightning bolt sketch top-right */}
        <motion.svg
          className="absolute top-20 right-20 w-20 h-20 text-primary/50"
          animate={{
            y: [0, 15, 0],
            x: [0, -10, 0],
          }}
          transition={{
            duration: 3.5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 0.5,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M55 10 L25 45 L45 48 L35 90 L75 45 L55 43 Z" />
        </motion.svg>

        {/* Checkmark circle sketch bottom-left */}
        <motion.svg
          className="absolute bottom-16 left-16 w-24 h-24 text-primary/30"
          animate={{
            scale: [1, 1.1, 1],
            rotate: [0, -10, 0],
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <circle cx="50" cy="50" r="40" strokeDasharray="5,5" />
          <path d="M30 50 L45 65 L70 35" strokeWidth="3" />
        </motion.svg>

        {/* Arrow sketch bottom-right */}
        <motion.svg
          className="absolute bottom-20 right-16 w-28 h-28 text-primary/40"
          animate={{
            x: [0, 10, 0],
            y: [0, -10, 0],
            rotate: [0, 5, 0],
          }}
          transition={{
            duration: 4.5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1.5,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M10 50 Q50 10 90 50" />
          <path d="M70 30 L90 50 L70 70" />
          <circle cx="15" cy="48" r="3" fill="currentColor" />
        </motion.svg>

        {/* Sparkle sketch middle-left */}
        <motion.svg
          className="absolute top-1/2 left-1/4 w-12 h-12 text-primary/60 hidden lg:block"
          animate={{
            scale: [1, 1.3, 1],
            rotate: [0, 180, 360],
            opacity: [0.6, 1, 0.6],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        >
          <path d="M50 10 L50 90 M10 50 L90 50" />
          <path d="M20 20 L80 80 M80 20 L20 80" />
        </motion.svg>

        {/* Scribble circle middle-right */}
        <motion.svg
          className="absolute top-1/3 right-1/4 w-20 h-20 text-primary/35 hidden lg:block"
          animate={{
            rotate: [0, 360],
            scale: [1, 1.15, 1],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 2,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <circle cx="50" cy="50" r="30" strokeDasharray="3,3" />
          <circle cx="50" cy="50" r="20" strokeDasharray="2,4" />
          <circle cx="50" cy="50" r="10" strokeDasharray="1,2" />
        </motion.svg>

        {/* Dotted heart sketch */}
        <motion.svg
          className="absolute bottom-1/3 left-1/3 w-16 h-16 text-primary/45 hidden md:block"
          animate={{
            y: [0, -15, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 0.8,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        >
          <path
            d="M50 85 C50 85, 20 60, 20 40 C20 25, 30 15, 40 15 C45 15, 50 20, 50 20 C50 20, 55 15, 60 15 C70 15, 80 25, 80 40 C80 60, 50 85, 50 85 Z"
            strokeDasharray="4,4"
          />
        </motion.svg>

        {/* Plus signs */}
        <motion.svg
          className="absolute top-1/4 right-1/3 w-10 h-10 text-primary/50 hidden lg:block"
          animate={{
            rotate: [0, 90, 0],
            opacity: [0.5, 1, 0.5],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1.2,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
        >
          <path d="M50 20 L50 80 M20 50 L80 50" />
        </motion.svg>

        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="mb-6 text-foreground text-center font-bold">
              Our Solutions
            </h1>
            <p className="text-foreground-secondary max-w-3xl mx-auto mb-8">
              Powerful AI-driven tools designed to supercharge your lead
              generation and business development. Extract, verify, and organize
              contact information at scale with industry-leading accuracy.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-8">
            {solutions.map((solution, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-card border border-card-border rounded-2xl p-8 hover:shadow-xl transition-all hover:-translate-y-1"
              >
                <div
                  className={`w-16 h-16 bg-linear-to-br ${solution.gradient} rounded-xl flex items-center justify-center mb-6`}
                >
                  <solution.icon className="w-8 h-8 text-white" />
                </div>

                <h2 className="mb-4 text-foreground">{solution.title}</h2>
                <p className="text-foreground-secondary mb-6">
                  {solution.description}
                </p>

                <div className="space-y-3 mb-8">
                  {solution.features.map((feature, fIndex) => (
                    <div key={fIndex} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                      <span className="text-foreground-secondary">
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => router.push(solution.page)}
                  className="w-full px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2 group"
                >
                  Learn More
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 px-4 bg-background-secondary">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="mb-4 text-foreground">Why Choose Leadbunker?</h2>
            <p className="text-foreground-secondary max-w-2xl mx-auto">
              Our platform is built on cutting-edge technology and industry best
              practices.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-8 h-8 text-primary" />
                </div>
                <h3 className="mb-3 text-foreground">{benefit.title}</h3>
                <p className="text-foreground-secondary">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="mb-4 text-foreground">How It Works</h2>
            <p className="text-foreground-secondary">
              Get started with Leadbunker in just three simple steps.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Input Your Source',
                description:
                  'Paste a URL or text containing the information you want to extract.',
              },
              {
                step: '02',
                title: 'AI Processing',
                description:
                  'Our advanced AI analyzes and extracts verified contact information instantly.',
              },
              {
                step: '03',
                title: 'Export & Use',
                description:
                  'Download your results in CSV, JSON, or integrate directly with your CRM.',
              },
            ].map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative"
              >
                <div className="text-primary/20 mb-4">{step.step}</div>
                <h3 className="mb-3 text-foreground">{step.title}</h3>
                <p className="text-foreground-secondary">{step.description}</p>
                {index < 2 && (
                  <ArrowRight className="hidden md:block absolute top-8 -right-4 w-8 h-8 text-primary/30" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-linear-to-br from-primary via-[#8b5cf6] to-[#ec4899] relative overflow-hidden">
        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjEiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-20"></div>

        {/* Floating Sketched Elements */}
        {/* Star sketch top-left */}
        <motion.svg
          className="absolute top-10 left-10 w-16 h-16 text-white/40"
          animate={{
            y: [0, -20, 0],
            rotate: [0, 15, 0],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <path d="M50 10 L55 40 L85 45 L60 65 L70 95 L50 80 L30 95 L40 65 L15 45 L45 40 Z" />
          <circle cx="50" cy="50" r="3" fill="currentColor" />
        </motion.svg>

        {/* Lightning bolt sketch top-right */}
        <motion.svg
          className="absolute top-20 right-20 w-20 h-20 text-white/50"
          animate={{
            y: [0, 15, 0],
            x: [0, -10, 0],
          }}
          transition={{
            duration: 3.5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 0.5,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M55 10 L25 45 L45 48 L35 90 L75 45 L55 43 Z" />
        </motion.svg>

        {/* Checkmark circle sketch bottom-left */}
        <motion.svg
          className="absolute bottom-16 left-16 w-24 h-24 text-white/30"
          animate={{
            scale: [1, 1.1, 1],
            rotate: [0, -10, 0],
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <circle cx="50" cy="50" r="40" strokeDasharray="5,5" />
          <path d="M30 50 L45 65 L70 35" strokeWidth="3" />
        </motion.svg>

        {/* Arrow sketch bottom-right */}
        <motion.svg
          className="absolute bottom-20 right-16 w-28 h-28 text-white/40"
          animate={{
            x: [0, 10, 0],
            y: [0, -10, 0],
            rotate: [0, 5, 0],
          }}
          transition={{
            duration: 4.5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1.5,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M10 50 Q50 10 90 50" />
          <path d="M70 30 L90 50 L70 70" />
          <circle cx="15" cy="48" r="3" fill="currentColor" />
        </motion.svg>

        {/* Sparkle sketch middle-left */}
        <motion.svg
          className="absolute top-1/2 left-1/4 w-12 h-12 text-white/60 hidden lg:block"
          animate={{
            scale: [1, 1.3, 1],
            rotate: [0, 180, 360],
            opacity: [0.6, 1, 0.6],
          }}
          transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        >
          <path d="M50 10 L50 90 M10 50 L90 50" />
          <path d="M20 20 L80 80 M80 20 L20 80" />
        </motion.svg>

        {/* Scribble circle middle-right */}
        <motion.svg
          className="absolute top-1/3 right-1/4 w-20 h-20 text-white/35 hidden lg:block"
          animate={{
            rotate: [0, 360],
            scale: [1, 1.15, 1],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 2,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <circle cx="50" cy="50" r="30" strokeDasharray="3,3" />
          <circle cx="50" cy="50" r="20" strokeDasharray="2,4" />
          <circle cx="50" cy="50" r="10" strokeDasharray="1,2" />
        </motion.svg>

        {/* Dotted heart sketch */}
        <motion.svg
          className="absolute bottom-1/3 left-1/3 w-16 h-16 text-white/45 hidden md:block"
          animate={{
            y: [0, -15, 0],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 0.8,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        >
          <path
            d="M50 85 C50 85, 20 60, 20 40 C20 25, 30 15, 40 15 C45 15, 50 20, 50 20 C50 20, 55 15, 60 15 C70 15, 80 25, 80 40 C80 60, 50 85, 50 85 Z"
            strokeDasharray="4,4"
          />
        </motion.svg>

        {/* Plus signs */}
        <motion.svg
          className="absolute top-1/4 right-1/3 w-10 h-10 text-white/50 hidden lg:block"
          animate={{
            rotate: [0, 90, 0],
            opacity: [0.5, 1, 0.5],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1.2,
          }}
          viewBox="0 0 100 100"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
        >
          <path d="M50 20 L50 80 M20 50 L80 50" />
        </motion.svg>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="mb-4 text-white font-bold">
              Ready to Transform Your Lead Generation?
            </h2>
            <p className="text-white/95 mb-8 max-w-2xl mx-auto">
              Start extracting high-quality leads today with our AI-powered
              platform.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <motion.button
                onClick={() => router.push('/auth/signup')}
                className="px-8 py-4 bg-white text-primary rounded-lg hover:bg-white/95 transition-all shadow-xl hover:shadow-2xl flex items-center gap-2"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                <Sparkles className="w-5 h-5" />
                Get Started Free
              </motion.button>
              <motion.button
                onClick={() => router.push('/pricing')}
                className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white border border-white/20 rounded-lg hover:bg-white/20 transition-colors flex items-center gap-2"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                <DollarSign className="w-5 h-5" />
                View Pricing
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
